import React, { Suspense } from "react"

import { Loader } from "shared/ui/Loader/Loader.component"
import { ACCENT } from "constants/brand"

interface GraphLoadBoundaryState {
  failed: boolean
}

/**
 * Catches a graph that couldn't load (its code is fetched on demand, and a new release or a
 * dropped connection can make that fail), so only the graph's place shows the failure and the
 * rest of the page keeps working. React only catches render errors in a class component.
 */
export class GraphLoadBoundary extends React.Component<
  { children: React.ReactNode },
  GraphLoadBoundaryState
> {
  state: GraphLoadBoundaryState = { failed: false }

  static getDerivedStateFromError(): GraphLoadBoundaryState {
    return { failed: true }
  }

  componentDidCatch(error: unknown): void {
    console.error("A graph couldn't load:", error)
  }

  render(): React.ReactNode {
    return this.state.failed ? (
      <div
        role="alert"
        className="flex h-full flex-col items-center justify-center gap-2 p-4 text-center text-[13px] text-slate-500"
      >
        <span>The graph couldn&apos;t load. Reloading the page usually fixes this.</span>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="dq-btn"
        >
          Reload page
        </button>
      </div>
    ) : (
      this.props.children
    )
  }
}

/**
 * Shows a graph loaded on demand: a centred loader while its code arrives, and a message with a
 * reload button if it can't be fetched.
 */
export const GraphSuspense: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <GraphLoadBoundary>
    <Suspense
      fallback={
        <div className="flex h-full items-center justify-center">
          <Loader fontSize={36} color={ACCENT} />
        </div>
      }
    >
      {children}
    </Suspense>
  </GraphLoadBoundary>
)

export default GraphSuspense
